import React, { useState, useEffect } from 'react';
import { __ } from './translations';

const YearDataManagement = () => {
    const [years, setYears] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editMode, setEditMode] = useState({});
    const [editData, setEditData] = useState({});

    const initialNewYearState = {
        finance_year: new Date().getFullYear(),
        finance_supporter_contribution: '',
        finance_ranges: [{ from: '', to: '', indicator: '', amount: '' }]
    };

    const [newYear, setNewYear] = useState(initialNewYearState);

    useEffect(() => {
        fetchYearData();
    }, []);

    const fetchYearData = async () => {
        try {
            const response = await fetch(`${boardMemberData.restURL}wp/v2/year_data?per_page=100`, {
                headers: {
                    'X-WP-Nonce': boardMemberData.nonce
                }
            });
            const data = await response.json();
            const sortedData = data.sort((a, b) => {
                const yearA = parseInt(a.title.rendered);
                const yearB = parseInt(b.title.rendered);
                return yearB - yearA;
            });
            setYears(sortedData);
        } catch (error) {
            console.error('Error fetching year data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEnterEditMode = (year) => {
        setEditMode({ ...editMode, [year.id]: true });
        setEditData({
            ...editData,
            [year.id]: {
                finance_year: year.meta.finance_year,
                finance_supporter_contribution: year.meta.finance_supporter_contribution,
                finance_ranges: [...year.meta.finance_ranges]
            }
        });
    };

    const handleCancelEdit = (yearId) => {
        setEditMode({ ...editMode, [yearId]: false });
        delete editData[yearId];
    };

    const handleEditChange = (yearId, field, value) => {
        setEditData({
            ...editData,
            [yearId]: {
                ...editData[yearId],
                [field]: value
            }
        });
    };

    const handleRangeChange = (yearId, index, field, value) => {
        const updatedRanges = [...editData[yearId].finance_ranges];
        updatedRanges[index] = {
            ...updatedRanges[index],
            [field]: value
        };
        handleEditChange(yearId, 'finance_ranges', updatedRanges);
    };

    const addRange = () => {
        setNewYear(prev => ({
          ...prev,
          finance_ranges: [...prev.finance_ranges, { from: '', to: '', indicator: '', amount: '' }]
        }));
      };

      const removeRange = (index) => {
        const updatedRanges = newYear.finance_ranges.filter((_, i) => i !== index);
        setNewYear(prev => ({
          ...prev,
          finance_ranges: updatedRanges
        }));
      };
    
      const removeRangeEditMode = (yearId, index) => {
        if (editData[yearId]) {
          const updatedRanges = editData[yearId].finance_ranges.filter((_, i) => i !== index);
          setEditData(prev => ({
            ...prev,
            [yearId]: {
              ...prev[yearId],
              finance_ranges: updatedRanges
            }
          }));
        }
      };

    const updateRange = (index, field, value) => {
        setNewYear(prev => ({
          ...prev,
          finance_ranges: prev.finance_ranges.map((range, i) => 
            i === index ? { ...range, [field]: value } : range
          )
        }));
      };


    const handleSubmitEdit = async (yearId) => {
        try {
            const response = await fetch(`${boardMemberData.restURL}wp/v2/year_data/${yearId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': boardMemberData.nonce
                },
                body: JSON.stringify({
                    meta: {
                        finance_supporter_contribution: editData[yearId].finance_supporter_contribution,
                        finance_ranges: editData[yearId].finance_ranges
                    }
                })
            });

            if (response.ok) {
                await fetchYearData();
                setEditMode({ ...editMode, [yearId]: false });
                delete editData[yearId];
            }
        } catch (error) {
            console.error('Error updating year data:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${boardMemberData.restURL}wp/v2/year_data`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': boardMemberData.nonce
                },
                body: JSON.stringify({
                    title: newYear.finance_year.toString(),
                    status: 'publish',
                    meta: {
                        finance_year: newYear.finance_year.toString(),
                        finance_supporter_contribution: newYear.finance_supporter_contribution,
                        finance_ranges: newYear.finance_ranges
                    }
                })
            });

            if (response.ok) {
                await fetchYearData();
                setShowAddForm(false);
                setNewYear(initialNewYearState);
            }
        } catch (error) {
            console.error('Error adding year data:', error);
        }
    };

    const handleDelete = async (yearId) => {
        if (!window.confirm(__('Are you sure you want to delete this year?'))) {
            return;
        }

        try {
            const response = await fetch(`${boardMemberData.restURL}wp/v2/year_data/${yearId}`, {
                method: 'DELETE',
                headers: {
                    'X-WP-Nonce': boardMemberData.nonce
                }
            });

            if (response.ok) {
                await fetchYearData();
            }
        } catch (error) {
            console.error('Error deleting year data:', error);
        }
    };

    if (loading) {
        return <div className="p-4">{__('Loading...')}</div>;
    }

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">{__('Year Data Management')}</h2>
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                >
                    {showAddForm ? __('Cancel') : __('Add New Year')}
                </button>
            </div>
            {showAddForm && (
        <form onSubmit={handleSubmit} className="mb-8 bg-white p-6 rounded-lg shadow">
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">
              {__('Year')}:
              <input
                type="number"
                value={newYear.finance_year}
                onChange={(e) => setNewYear(prev => ({ ...prev, finance_year: parseInt(e.target.value) }))}
                className="mt-1 block w-full rounded border-gray-300 shadow-sm p-2"
                required
              />
            </label>
          </div>

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">
              {__('Supporter Contribution')}:
              <input
                type="number"
                step="0.01"
                value={newYear.finance_supporter_contribution}
                onChange={(e) => setNewYear(prev => ({ ...prev, finance_supporter_contribution: e.target.value }))}
                className="mt-1 block w-full rounded border-gray-300 shadow-sm p-2"
                required
              />
            </label>
          </div>

          <div className="mb-4">
            <h3 className="text-lg font-medium mb-4">{__('Income Ranges')}</h3>
            {newYear.finance_ranges.map((range, index) => (
              <div key={index} className="flex gap-4 mb-4 p-4 bg-gray-50 rounded">
                <div>
                  <label className="block text-sm font-medium">
                    {__('From')}:
                    <input
                      type="number"
                      step="0.01"
                      value={range.from}
                      onChange={(e) => updateRange(index, 'from', e.target.value)}
                      className="mt-1 block w-full rounded border-gray-300 shadow-sm p-2"
                      required
                    />
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium">
                    {__('To')}:
                    <input
                      type="number"
                      step="0.01"
                      value={range.to}
                      onChange={(e) => updateRange(index, 'to', e.target.value)}
                      className="mt-1 block w-full rounded border-gray-300 shadow-sm p-2"
                      required
                    />
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium">
                    {__('Indicator')}:
                    <input
                      type="number"
                      step="0.01"
                      value={range.indicator}
                      onChange={(e) => updateRange(index, 'indicator', e.target.value)}
                      className="mt-1 block w-full rounded border-gray-300 shadow-sm p-2"
                      required
                    />
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium">
                    {__('Amount')}:
                    <input
                      type="number"
                      step="0.01"
                      value={range.amount}
                      onChange={(e) => updateRange(index, 'amount', e.target.value)}
                      className="mt-1 block w-full rounded border-gray-300 shadow-sm p-2"
                      required
                    />
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => removeRange(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  {__('Remove')}
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addRange}
              className="text-blue-500 hover:text-blue-700"
            >
              {__('Add Range')}
            </button>
          </div>

          <button
            type="submit"
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
          >
            {__('Save Year Data')}
          </button>
        </form>
      )}
            <div className="grid gap-6">
                {years.map((year) => (
                    <div key={year.id} className="bg-white rounded-lg p-6 border-b border-gray-200">
                        {editMode[year.id] ? (
                            // Edit Mode
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-xl font-semibold">
                                        {__('Year')}: {year.title.rendered}
                                    </h3>
                                    <div className="space-x-2">
                                        <button
                                            onClick={() => handleSubmitEdit(year.id)}
                                            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                                        >
                                            {__('Save')}
                                        </button>
                                        <button
                                            onClick={() => handleCancelEdit(year.id)}
                                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                                        >
                                            {__('Cancel')}
                                        </button>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {__('Calculated Contribution')}:
                                    </label>
                                    <input
                                        type="number"
                                        value={editData[year.id].finance_supporter_contribution}
                                        onChange={(e) => handleEditChange(year.id, 'finance_supporter_contribution', e.target.value)}
                                        className="w-full p-2 border rounded"
                                    />
                                </div>

                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-medium">{__('Ranges')}:</h4>
                                        <button
                                            onClick={() => addRange(year.id)}
                                            className="text-blue-500 hover:text-blue-700"
                                        >
                                            {__('Add Range')}
                                        </button>
                                    </div>

                                    {editData[year.id].finance_ranges.map((range, index) => (
                                        <div key={index} className="grid grid-cols-5 gap-4 bg-gray-50 p-4 rounded">
                                            <div>
                                                <label className="block text-sm text-gray-700">{__('From:')}</label>
                                                <input
                                                    type="number"
                                                    value={range.from}
                                                    onChange={(e) => handleRangeChange(year.id, index, 'from', e.target.value)}
                                                    className="w-full p-2 border rounded"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-700">{__('To:')}</label>
                                                <input
                                                    type="number"
                                                    value={range.to}
                                                    onChange={(e) => handleRangeChange(year.id, index, 'to', e.target.value)}
                                                    className="w-full p-2 border rounded"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-700">{__('Indicator:')}</label>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={range.indicator}
                                                    onChange={(e) => handleRangeChange(year.id, index, 'indicator', e.target.value)}
                                                    className="w-full p-2 border rounded"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-700">{__('Amount:')}</label>
                                                <input
                                                    type="number"
                                                    value={range.amount}
                                                    onChange={(e) => handleRangeChange(year.id, index, 'amount', e.target.value)}
                                                    className="w-full p-2 border rounded"
                                                />
                                            </div>
                                            <div className="flex items-end">
                                                <button
                                                    onClick={() => removeRangeEditMode(year.id, index)}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    {__('Remove')}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            // View Mode
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <div>
                                        <h3 className="text-xl font-semibold mb-2">
                                            {__('Year')}: {year.title.rendered}
                                        </h3>
                                        <p className="text-gray-600">
                                            {__('Supporter Contribution')}: {year.meta.finance_supporter_contribution} PLN
                                        </p>
                                    </div>
                                    <div className="space-x-2">
                                        <button
                                            onClick={() => handleEnterEditMode(year)}
                                            className="text-blue-500 hover:text-blue-700"
                                        >
                                            {__('Edit')}
                                        </button>
                                        <button
                                            onClick={() => handleDelete(year.id)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            {__('Delete')}
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <h4 className="font-medium mb-2">{__('Ranges')}:</h4>
                                    {year.meta.finance_ranges && year.meta.finance_ranges.map((range, index) => (
                                        <div key={index} className="bg-gray-50 p-4 rounded mb-2">
                                            <p className='mb-0'>
                                                {__('From (PLN million):')} {range.from} | {__('To (PLN million):')} {range.to} | {__('Contribution Indicator:')} {range.indicator} | {__('Contribution Amount:')} {range.amount}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default YearDataManagement;